import React from 'react';
import { Link } from 'react-router-dom';
import logo from './classlabs.png';

const BRAND = {
  teal: '#75B9BE',
  cream: '#FDF6EC',
  charcoal: '#2D4042',
  coral: '#F28482',
  white: '#FFFFFF'
};

const sectionTitleStyle = {
  fontFamily: 'Nunito, sans-serif',
  fontWeight: 900,
  color: BRAND.charcoal,
  marginTop: '28px',
  marginBottom: '12px',
  fontSize: '1.4rem'
};

const paragraphStyle = {
  margin: '0 0 14px 0',
  color: '#4C6163',
  lineHeight: 1.8,
  fontSize: '1rem'
};

const listStyle = {
  margin: '0 0 14px 20px',
  color: '#4C6163',
  lineHeight: 1.8,
  fontSize: '1rem'
};

const PrivacyPolicy = () => {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: BRAND.cream,
        fontFamily: "'Open Sans', sans-serif",
        color: BRAND.charcoal
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Open+Sans:wght@500;700&display=swap');
        `}
      </style>

      <header
        style={{
          borderBottom: `3px solid ${BRAND.charcoal}`,
          background: BRAND.white,
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}
      >
        <div
          style={{
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '16px 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img
              src={logo}
              alt="ClassLabs"
              style={{ height: '52px', width: 'auto', display: 'block', mixBlendMode: 'multiply' }}
            />
          </Link>
          <Link
            to="/"
            style={{
              fontFamily: 'Nunito, sans-serif',
              fontWeight: 800,
              textDecoration: 'none',
              color: BRAND.charcoal,
              border: `2px solid ${BRAND.charcoal}`,
              borderBottom: `4px solid ${BRAND.charcoal}`,
              borderRadius: '12px',
              padding: '8px 14px',
              background: '#fff'
            }}
          >
            Back to Home
          </Link>
        </div>
      </header>

      <main style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px 60px' }}>
        <div
          style={{
            background: BRAND.white,
            border: `3px solid ${BRAND.charcoal}`,
            borderRadius: '18px',
            boxShadow: `8px 8px 0 ${BRAND.teal}`,
            padding: '28px'
          }}
        >
          <h1
            style={{
              marginTop: 0,
              marginBottom: '20px',
              fontFamily: 'Nunito, sans-serif',
              fontWeight: 900,
              fontSize: '2.1rem',
              color: BRAND.charcoal
            }}
          >
            Privacy Policy
          </h1>

          <h2 style={sectionTitleStyle}>1. Introduction</h2>
          <p style={paragraphStyle}>
            Welcome to ClassLabs. This Privacy Policy is issued on behalf of ClassCompass Ltd (trading as
            ClassLabs). When we mention &quot;ClassLabs,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot; in this policy, we are referring to
            ClassCompass Ltd, the relevant legal entity responsible for processing your data.
          </p>
          <p style={paragraphStyle}>
            We provide an adaptive educational technology platform designed to assist teachers in scaffolding and
            personalizing learning materials for students (the &quot;Service&quot;). We are committed to protecting the privacy
            and security of all our users. This Privacy Policy explains how we collect, use, store, and protect
            personal data in compliance with the UK General Data Protection Regulation (UK GDPR), the Data Protection
            Act 2018, and the Information Commissioner&apos;s Office (ICO) Age Appropriate Design Code.
          </p>

          <h2 style={sectionTitleStyle}>2. Our Role: Data Controller vs. Data Processor</h2>
          <p style={paragraphStyle}>It is important to understand our legal role regarding the data we process:</p>
          <ul style={listStyle}>
            <li>
              <strong>For Teachers and School Administrators:</strong> When you register for an account or contact us,
              we act as the Data Controller of your personal data.
            </li>
            <li>
              <strong>For Students (Children):</strong> We act strictly as a Data Processor. The School or the Teacher
              using our platform acts as the Data Controller. We process student data solely under the instruction and
              authority of the School to provide the educational service. We do not require direct parental consent,
              as the School relies on its public task or legitimate interest to educate the child.
            </li>
          </ul>

          <h2 style={sectionTitleStyle}>3. What Information We Collect</h2>
          <p style={paragraphStyle}>
            We practice strict Data Minimization. We only collect what is absolutely necessary to deliver our
            educational service.
          </p>
          <p style={paragraphStyle}>
            <strong>A. Data from Teachers/Administrators (Adults)</strong>
          </p>
          <ul style={listStyle}>
            <li>
              <strong>Identity &amp; Contact Data:</strong> First name, last name, email address, school affiliation,
              and role.
            </li>
            <li>
              <strong>Authentication Data:</strong> Hashed passwords and registration codes.
            </li>
            <li>
              <strong>Usage Data:</strong> Login timestamps, IP addresses, browser types, and session duration.
            </li>
          </ul>
          <p style={paragraphStyle}>
            <strong>B. Data from Students (Children)</strong>
          </p>
          <p style={paragraphStyle}>
            Students do not create accounts on our platform or set passwords. They access materials via temporary
            &quot;magic links&quot; generated by their teachers. We process the following data, which is strictly inputted and
            managed by the Teacher:
          </p>
          <ul style={listStyle}>
            <li>
              <strong>Basic Educational Profile:</strong> First name (or nickname), age, reading age, and primary
              language/EAL status.
            </li>
            <li>
              <strong>Contact Data (Optional):</strong> A student email address may be provided by the Teacher strictly
              for the functional purpose of routing the &quot;magic link&quot; directly to the student. We do not use student
              emails for marketing, nor do we require students to verify an account.
            </li>
            <li>
              <strong>Special Category Data:</strong> Specific Special Educational Needs (SEN) conditions (e.g.,
              Dyslexia, Autism) to adapt the learning materials. This is processed under the substantial public
              interest of providing tailored education.
            </li>
            <li>
              <strong>Performance Data:</strong> Quiz answers, scores, interaction logs, and completion times.
            </li>
          </ul>

          <h2 style={sectionTitleStyle}>4. How We Use the Data and Our Lawful Basis</h2>
          <p style={paragraphStyle}>We process personal data based on the following lawful bases:</p>
          <ul style={listStyle}>
            <li>
              <strong>To provide the service (Contract / School Authority):</strong> Creating teacher accounts,
              authenticating users, and generating adapted lesson materials for students.
            </li>
            <li>
              <strong>For security and platform integrity (Legitimate Interest):</strong> Monitoring system logs to
              prevent unauthorized access.
            </li>
            <li>
              <strong>To improve our service (Legitimate Interest):</strong> Analyzing aggregate, anonymised usage
              trends to improve our educational algorithms.
            </li>
          </ul>

          <h2 style={sectionTitleStyle}>5. How We Store and Protect Your Data</h2>
          <p style={paragraphStyle}>We implement robust &quot;Privacy by Design&quot; security measures:</p>
          <ul style={listStyle}>
            <li>
              <strong>Secure Cloud Hosting:</strong> All primary application data is stored securely using
              enterprise-grade cloud hosting providers. Our servers are located strictly within the UK or the European
              Economic Area (EEA), ensuring full compliance with UK GDPR international transfer adequacy decisions.
            </li>
            <li>
              <strong>Student Access:</strong> We eliminate the risk of compromised child credentials by using
              accountless &quot;magic links&quot; for all student access, ensuring no child passwords are ever stored or
              transmitted.
            </li>
          </ul>

          <h2 style={sectionTitleStyle}>6. Third-Party Sub-Processors</h2>
          <p style={paragraphStyle}>
            To provide our service, we engage trusted third-party sub-processors. We execute strict Data Processing
            Agreements with all providers to ensure data security.
          </p>
          <ul style={listStyle}>
            <li>
              <strong>Authentication Services:</strong> Secure third-party authentication is used strictly for adult
              Teacher/Admin accounts. Student data is never passed to these authentication providers.
            </li>
            <li>
              <strong>Generative AI Providers:</strong> We utilize artificial intelligence services to adapt
              educational content to a student&apos;s reading age and SEN condition. We never transmit a student&apos;s
              Personally Identifiable Information (PII) to our AI providers. All parameters sent for content
              generation are strictly pseudonymised and cannot be linked back to an individual student.
            </li>
          </ul>

          <h2 style={sectionTitleStyle}>7. Data Retention Policy</h2>
          <p style={paragraphStyle}>We do not hold data for longer than is necessary. Our retention schedule is as follows:</p>
          <ul style={listStyle}>
            <li>
              <strong>Student Data (Profiles, SEN data, Emails, Performance logs):</strong> Retained only for the
              duration of the active academic year. Data is permanently erased upon explicit deletion request by the
              teacher, when the school&apos;s contract terminates, or routinely at the conclusion of the UK academic year
              (August).
            </li>
            <li>
              <strong>Teacher/Admin Accounts:</strong> Retained for the duration of active use. Accounts that are
              inactive for a continuous period of 12 months will be permanently deleted following a warning
              notification.
            </li>
            <li>
              <strong>System &amp; Security Logs:</strong> Retained for a rolling period of 90 days for debugging
              purposes, after which they are automatically purged.
            </li>
          </ul>

          <h2 style={sectionTitleStyle}>8. Your Data Protection Rights</h2>
          <p style={paragraphStyle}>Under UK GDPR, individuals have rights including:</p>
          <ul style={listStyle}>
            <li>The right to access the personal data we hold about you.</li>
            <li>The right to request correction of inaccurate data.</li>
            <li>The right to request erasure of your data (the &quot;right to be forgotten&quot;).</li>
            <li>The right to restrict or object to our processing.</li>
          </ul>
          <p style={paragraphStyle}>
            <strong>Note for Parents/Students:</strong> Because the School is the Data Controller for student data, any
            requests regarding access, correction, or deletion of a student&apos;s data should be directed to the
            student&apos;s Teacher or School. We will fully cooperate with the School to fulfill these requests.
          </p>

          <h2 id="cookies" style={sectionTitleStyle}>9. Cookies and Similar Technologies</h2>
          <p style={paragraphStyle}>
            Cookies are small files stored on your device. We use a small number of them, and we group
            them into the categories below. <strong>Analytics and marketing cookies are only ever used on
            our public marketing website, and are set only if you agree via our cookie banner. We do not
            use analytics or advertising cookies anywhere in the student-facing part of the Service.</strong>
          </p>
          <ul style={listStyle}>
            <li>
              <strong>Strictly necessary (always on):</strong> Used to keep you securely signed in and to
              run core features (e.g. Firebase Authentication). These do not require consent because the
              Service will not work without them.
            </li>
            <li>
              <strong>Analytics (consent required):</strong> Google Analytics 4, provided by Google, which
              sets cookies such as <code>_ga</code> and <code>_ga_&lt;id&gt;</code> (typically lasting up to
              2 years). These help us understand, in aggregate, how visitors use our marketing site so we
              can improve it.
            </li>
            <li>
              <strong>Marketing (consent required):</strong> The Meta (Facebook) Pixel, provided by Meta
              Platforms, which sets cookies such as <code>_fbp</code> (typically lasting around 90 days).
              These help us measure and improve the relevance of our advertising. We do not build or target
              advertising audiences based on any special category data such as SEN or disability.
            </li>
            <li>
              <strong>Embedded video:</strong> Our marketing videos are embedded from Vimeo in
              &quot;Do Not Track&quot; mode, so the video player does not set tracking cookies.
            </li>
          </ul>
          <p style={paragraphStyle}>
            <strong>Managing your choices:</strong> You can accept or reject non-essential cookies using the
            banner shown on our website. You can withdraw or change your consent at any time by clearing the
            cookies stored by our site in your browser settings, after which the banner will appear again on
            your next visit. Rejecting non-essential cookies will not affect your ability to use the Service.
          </p>

          <h2 style={sectionTitleStyle}>10. Contact Us</h2>
          <p style={paragraphStyle}>
            If you have any questions about this Privacy Policy, our data practices, or wish to exercise your rights,
            please contact us at:
          </p>
          <p style={paragraphStyle}>
            <strong>Email:</strong> info@classlabs.co.uk
            <br />
            <strong>Company:</strong> ClassCompass Ltd (Company No. 16701850)
            <br />
            <strong>Registered Address:</strong> 128 City Road, London, EC1V 2NX, England
          </p>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
